import { useRef, useState } from 'preact/hooks'
import type { ComponentChildren, JSX } from 'preact'

type Plateforme = 'claude' | 'openai' | 'copilot'

type Copie = {
  id: string
  etat: 'reussie' | 'echouee'
}

const plateformes: Array<{ id: Plateforme; libelle: string }> = [
  { id: 'claude', libelle: 'Claude' },
  { id: 'openai', libelle: 'ChatGPT / Codex' },
  { id: 'copilot', libelle: 'GitHub Copilot' },
]

const outils = [
  {
    nom: 'lister_evenements',
    description:
      'Liste jusqu’à 100 événements et leurs états actuels, avec un indicateur si le résultat est tronqué.',
  },
  {
    nom: 'lister_reservations',
    description:
      'Liste jusqu’à 100 réservations avec le nom et le courriel du demandeur, sans lien privé ni jeton d’action.',
  },
  {
    nom: 'rapport_financier',
    description:
      'Lit le rapport financier réel sur 366 jours au plus; chaque liste de détails est plafonnée à 500 éléments et signale sa troncature.',
  },
  {
    nom: 'consulter_paiements_stripe',
    description:
      'Consulte les ventes Stripe liées à Funo, les paniers abandonnés, les remboursements et les confirmations, avec les mêmes filtres que le tableau de bord.',
  },
  {
    nom: 'synchroniser_paiements_stripe',
    description:
      'Vérifie les paiements auprès de Stripe et met à jour Funo. Requiert une autorisation OAuth d’écriture financière.',
  },
  {
    nom: 'rembourser_paiement_stripe',
    description:
      'Demande un remboursement Stripe sur un paiement Funo, sur votre demande explicite et avec une autorisation OAuth d’écriture financière. Une demande en attente n’est pas encore un remboursement confirmé.',
  },
]

function chaineShell(valeur: string) {
  return `'${valeur.replaceAll("'", `'"'"'`)}'`
}

function avertissementAdresse(mcpUrl: string) {
  try {
    const url = new URL(mcpUrl)
    const hote = url.hostname.toLowerCase()
    const locale =
      hote === 'localhost' ||
      hote === '127.0.0.1' ||
      hote === '::1' ||
      hote === '[::1]' ||
      hote === '0.0.0.0' ||
      hote.endsWith('.localhost')
    if (locale)
      return {
        titre: 'Adresse locale',
        texte:
          'Claude et les agents GitHub hébergés ne peuvent pas joindre localhost. Pour ces clients, publiez Funo sur une adresse HTTPS accessible depuis Internet.',
      }
    if (url.protocol !== 'https:')
      return {
        titre: 'Adresse non sécurisée',
        texte:
          'Utilisez une adresse publique en HTTPS avant de connecter un service hébergé. OAuth et les clients distants ne doivent pas utiliser HTTP hors de localhost.',
      }
  } catch {
    return {
      titre: 'Adresse non publique',
      texte:
        'Utilisez une adresse HTTPS complète et accessible depuis Internet pour les clients hébergés.',
    }
  }
  return null
}

function Extrait({
  id,
  titre,
  contenu,
  copie,
  copier,
}: {
  id: string
  titre: string
  contenu: string
  copie: Copie | null
  copier(id: string, contenu: string): void
}) {
  const reussie = copie?.id === id && copie.etat === 'reussie'
  return (
    <div>
      <p>
        <strong>{titre}</strong>
      </p>
      <pre>
        <code>{contenu}</code>
      </pre>
      <button
        type="button"
        class="ac-button"
        aria-label={`Copier ${titre.toLocaleLowerCase('fr-CA')}`}
        onClick={() => copier(id, contenu)}
      >
        {reussie ? 'Copié' : 'Copier'}
      </button>
    </div>
  )
}

function Liens({ children }: { children: ComponentChildren }) {
  return (
    <p>
      <strong>Documentation officielle : </strong>
      {children}
    </p>
  )
}

/**
 * Guide autonome de connexion au serveur MCP Funo.
 *
 * Le composant ne contient volontairement aucune clé ou valeur secrète : les
 * clients qui le permettent ouvrent le parcours OAuth dans le navigateur.
 */
export function McpAide({ mcpUrl }: { mcpUrl: string }) {
  const [plateforme, setPlateforme] = useState<Plateforme>('claude')
  const [copie, setCopie] = useState<Copie | null>(null)
  const boutons = useRef<Array<HTMLButtonElement | null>>([])
  const avertissement = avertissementAdresse(mcpUrl)
  const claudeCode = [
    `claude mcp add --transport http funo ${chaineShell(mcpUrl)}`,
    'claude mcp login funo',
    'claude mcp get funo',
  ].join('\n')
  const codexCli = [
    `codex mcp add funo --url ${chaineShell(mcpUrl)}`,
    'codex mcp login funo',
    'codex mcp list',
  ].join('\n')
  const codexToml = `[mcp_servers.funo]\nurl = ${JSON.stringify(mcpUrl)}`
  const vscodeJson = JSON.stringify(
    {
      servers: {
        funo: {
          type: 'http',
          url: mcpUrl,
        },
      },
    },
    null,
    2,
  )
  const copilotCli = [
    `copilot mcp add --transport http funo ${chaineShell(mcpUrl)}`,
    '/mcp auth funo',
    '/mcp show funo',
  ].join('\n')

  const copier = (id: string, contenu: string) => {
    const pressePapiers = globalThis.navigator?.clipboard
    if (!pressePapiers?.writeText) {
      setCopie({ id, etat: 'echouee' })
      return
    }
    void pressePapiers.writeText(contenu).then(
      () => setCopie({ id, etat: 'reussie' }),
      () => setCopie({ id, etat: 'echouee' }),
    )
  }

  const changerParClavier = (
    evenement: JSX.TargetedKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let prochain = index
    if (evenement.key === 'ArrowRight')
      prochain = (index + 1) % plateformes.length
    else if (evenement.key === 'ArrowLeft')
      prochain = (index - 1 + plateformes.length) % plateformes.length
    else if (evenement.key === 'Home') prochain = 0
    else if (evenement.key === 'End') prochain = plateformes.length - 1
    else return
    evenement.preventDefault()
    setPlateforme(plateformes[prochain]!.id)
    boutons.current[prochain]?.focus()
  }

  return (
    <section class="ac-panel ac-instructions" aria-labelledby="mcp-aide-titre">
      <h2 id="mcp-aide-titre">Connecter Funo à un assistant</h2>
      <p>
        Consultez vos événements, vos réservations, vos rapports financiers et
        vos ventes Stripe depuis un assistant compatible MCP. Vous pouvez aussi
        autoriser la synchronisation et les remboursements Stripe. La connexion s’effectue avec OAuth :
        aucune clé privée n’est à copier dans un fichier.
      </p>

      <div aria-label="Caractéristiques de la connexion">
        <span class="ac-badge">Streamable HTTP</span>{' '}
        <span class="ac-badge">OAuth</span>{' '}
        <span class="ac-badge">Lecture par défaut</span>{' '}
        <span class="ac-badge">6 outils</span>
      </div>

      <label for="mcp-aide-url">Adresse du serveur MCP</label>
      <div class="ac-copy-field">
        <input
          id="mcp-aide-url"
          readOnly
          value={mcpUrl}
          onFocus={(evenement) => evenement.currentTarget.select()}
        />
        <button
          type="button"
          class="ac-button"
          aria-label="Copier l’adresse du serveur MCP"
          onClick={() => copier('adresse', mcpUrl)}
        >
          {copie?.id === 'adresse' && copie.etat === 'reussie'
            ? 'Copié'
            : 'Copier'}
        </button>
      </div>

      {avertissement && (
        <aside
          class="ac-notice erreur"
          aria-label="Avertissement sur l’adresse MCP"
        >
          <span>
            <strong>{avertissement.titre}.</strong> {avertissement.texte}
          </span>
        </aside>
      )}

      <p>
        OAuth ouvre Funo dans votre navigateur. Vous vous connectez et autorisez
        l’accès; l’assistant ne reçoit jamais votre mot de passe. N’ajoutez
        aucune clé, aucun jeton ni autre secret aux configurations ci-dessous.
        La première connexion demande seulement la lecture des événements;
        l’assistant redemande votre accord au moment d’utiliser les réservations
        ou les finances.
      </p>

      <section aria-labelledby="mcp-capacites-titre">
        <h3 id="mcp-capacites-titre">Ce que Funo expose</h3>
        <ul>
          {outils.map((outil) => (
            <li key={outil.nom}>
              <code>{outil.nom}</code> — {outil.description}
            </li>
          ))}
        </ul>
        <p>
          Les quatre outils de consultation restent en lecture seule. Funo n’expose aucune
          ressource MCP et aucun prompt MCP. Seuls les outils autorisés pour
          votre compte apparaissent dans l’assistant.
        </p>
        <p>
          La synchronisation et le remboursement demandent votre accord OAuth
          explicite pour <code>finances.modifier</code>. Les connexions existantes
          restent limitées à leurs autorisations actuelles : reconnectez
          l’assistant et acceptez ces actions si vous souhaitez les utiliser.
          Les clés API personnelles restent en lecture seule.
        </p>
      </section>

      <h3 id="mcp-client-titre">Choisir un assistant</h3>
      <div class="ac-tabs" role="tablist" aria-labelledby="mcp-client-titre">
        {plateformes.map((item, index) => (
          <button
            key={item.id}
            ref={(element) => {
              boutons.current[index] = element
            }}
            id={`mcp-onglet-${item.id}`}
            type="button"
            role="tab"
            aria-controls={`mcp-panneau-${item.id}`}
            aria-selected={plateforme === item.id}
            aria-pressed={plateforme === item.id}
            tabIndex={plateforme === item.id ? 0 : -1}
            onClick={() => setPlateforme(item.id)}
            onKeyDown={(evenement) => changerParClavier(evenement, index)}
          >
            {item.libelle}
          </button>
        ))}
      </div>

      <section
        id="mcp-panneau-claude"
        role="tabpanel"
        aria-labelledby="mcp-onglet-claude"
        tabIndex={0}
        hidden={plateforme !== 'claude'}
      >
          <h3>Claude</h3>
          <p>
            <span class="ac-badge">
              Support documenté — validation Funo requise
            </span>
          </p>
          <h4>Claude sur le Web et application de bureau</h4>
          <ol>
            <li>
              Ouvrez <strong>Personnaliser &gt; Connecteurs</strong>. Sur Team
              ou Enterprise, un propriétaire doit d’abord ajouter le connecteur
              dans les paramètres de l’organisation.
            </li>
            <li>
              Ajoutez un connecteur personnalisé nommé <strong>Funo</strong>,
              collez l’adresse MCP, puis terminez l’autorisation OAuth.
            </li>
            <li>Activez Funo depuis le menu des outils de la conversation.</li>
          </ol>
          <p>
            Claude appelle les connecteurs distants depuis le nuage, y compris
            depuis l’application de bureau. Une adresse locale, privée ou
            accessible seulement par VPN ne fonctionnera pas.
          </p>
          <h4>Claude Code</h4>
          <Extrait
            id="claude-code"
            titre="les commandes Claude Code"
            contenu={claudeCode}
            copie={copie}
            copier={copier}
          />
          <p>
            Utilisez <code>/mcp</code> dans Claude Code pour vérifier la
            connexion ou relancer l’authentification. Pour effacer les
            identifiants OAuth conservés localement par Claude Code, lancez{' '}
            <code>claude mcp logout funo</code>. Révoquez séparément l’accès
            côté Funo dans « Connexions OAuth actives » ci-dessus.
          </p>
          <Liens>
            <a
              href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp"
              target="_blank"
              rel="noreferrer"
            >
              Connecteurs distants
            </a>{' '}
            ·{' '}
            <a
              href="https://claude.com/docs/connectors/building/authentication"
              target="_blank"
              rel="noreferrer"
            >
              OAuth pour les connecteurs
            </a>{' '}
            ·{' '}
            <a
              href="https://code.claude.com/docs/en/mcp"
              target="_blank"
              rel="noreferrer"
            >
              MCP dans Claude Code
            </a>
          </Liens>
      </section>

      <section
        id="mcp-panneau-openai"
        role="tabpanel"
        aria-labelledby="mcp-onglet-openai"
        tabIndex={0}
        hidden={plateforme !== 'openai'}
      >
          <h3>ChatGPT Desktop et Codex</h3>
          <p>
            <span class="ac-badge">
              Support documenté — validation Funo requise
            </span>
          </p>
          <h4>Application de bureau ChatGPT</h4>
          <ol>
            <li>
              Ouvrez <strong>Réglages &gt; Serveurs MCP</strong>, puis choisissez{' '}
              <strong>Ajouter un serveur</strong>.
            </li>
            <li>
              Nommez-le <strong>Funo</strong>, choisissez{' '}
              <strong>Streamable HTTP</strong> et collez l’adresse MCP.
            </li>
            <li>
              Enregistrez, redémarrez l’application, puis choisissez{' '}
              <strong>Authentifier</strong>. La commande <code>/mcp</code>
              affiche ensuite les serveurs connectés.
            </li>
          </ol>
          <p>
            ChatGPT Desktop, Codex CLI et l’extension IDE partagent cette
            configuration lorsqu’ils utilisent le même hôte Codex.
          </p>
          <h4>Codex CLI</h4>
          <Extrait
            id="codex-cli"
            titre="les commandes Codex CLI"
            contenu={codexCli}
            copie={copie}
            copier={copier}
          />
          <Extrait
            id="codex-toml"
            titre="la configuration config.toml"
            contenu={codexToml}
            copie={copie}
            copier={copier}
          />
          <p>
            Placez cette section dans <code>~/.codex/config.toml</code>, ou dans
            {' '}<code>.codex/config.toml</code> pour un projet de confiance,
            puis lancez <code>codex mcp login funo</code>. Utilisez{' '}
            <code>codex mcp logout funo</code> pour supprimer les identifiants
            OAuth enregistrés localement avant une reconnexion. Révoquez
            séparément l’accès côté Funo dans « Connexions OAuth actives ».
          </p>
          <p>
            <strong>ChatGPT sur le Web :</strong> il ne lit pas la configuration
            Codex locale et n’accepte pas directement une adresse MCP. Dans un
            espace ChatGPT Work hébergé, les outils MCP sont fournis par un
            plugin installé.
          </p>
          <Liens>
            <a
              href="https://learn.chatgpt.com/docs/extend/mcp"
              target="_blank"
              rel="noreferrer"
            >
              MCP dans ChatGPT et Codex
            </a>{' '}
            ·{' '}
            <a
              href="https://learn.chatgpt.com/docs/config-file/config-reference"
              target="_blank"
              rel="noreferrer"
            >
              Référence de configuration Codex
            </a>
          </Liens>
      </section>

      <section
        id="mcp-panneau-copilot"
        role="tabpanel"
        aria-labelledby="mcp-onglet-copilot"
        tabIndex={0}
        hidden={plateforme !== 'copilot'}
      >
          <h3>GitHub Copilot</h3>
          <p>
            <span class="ac-badge">
              Support documenté — validation Funo requise
            </span>
          </p>
          <h4>Visual Studio Code</h4>
          <p>
            Utilisez VS Code 1.106 ou une version ultérieure avec GitHub Copilot.
            Ajoutez cette configuration dans <code>.vscode/mcp.json</code> pour
            le projet, ou dans votre profil pour tous vos espaces de travail.
          </p>
          <Extrait
            id="vscode-json"
            titre="la configuration VS Code"
            contenu={vscodeJson}
            copie={copie}
            copier={copier}
          />
          <p>
            Démarrez le serveur depuis la vue MCP de VS Code, puis suivez le
            parcours OAuth dans le navigateur. Une politique d’organisation
            peut devoir autoriser MCP avec Copilot Business ou Enterprise.
          </p>
          <h4>GitHub Copilot CLI</h4>
          <Extrait
            id="copilot-cli"
            titre="les commandes GitHub Copilot CLI"
            contenu={copilotCli}
            copie={copie}
            copier={copier}
          />
          <p>
            Les commandes <code>/mcp auth funo</code> et{' '}
            <code>/mcp show funo</code> s’exécutent dans la session interactive
            de Copilot CLI.
          </p>
          <p>
            <strong>Agents hébergés sur GitHub :</strong> Copilot coding agent
            et Copilot code review acceptent uniquement les outils MCP, mais ne
            prennent actuellement pas en charge OAuth pour un serveur MCP
            distant. Ne configurez pas Funo dans ces agents tant qu’une méthode
            adaptée et sécurisée n’est pas disponible.
          </p>
          <Liens>
            <a
              href="https://code.visualstudio.com/docs/agent-customization/mcp-servers"
              target="_blank"
              rel="noreferrer"
            >
              Serveurs MCP dans VS Code
            </a>{' '}
            ·{' '}
            <a
              href="https://docs.github.com/en/copilot/how-tos/copilot-cli/customize-copilot/add-mcp-servers"
              target="_blank"
              rel="noreferrer"
            >
              MCP dans Copilot CLI
            </a>{' '}
            ·{' '}
            <a
              href="https://docs.github.com/en/copilot/how-tos/copilot-on-github/customize-copilot/configure-mcp-servers"
              target="_blank"
              rel="noreferrer"
            >
              Limites des agents GitHub
            </a>
          </Liens>
      </section>

      <section aria-labelledby="mcp-essai-titre">
        <h3 id="mcp-essai-titre">Essayer la connexion</h3>
        <p>
          Dans votre assistant, demandez :{' '}
          <q>Quels événements Funo sont actuellement publiés ?</q>
        </p>
      </section>

      <p class="ac-sr" role="status" aria-live="polite">
        {copie?.etat === 'reussie'
          ? 'Copié dans le presse-papiers.'
          : copie?.etat === 'echouee'
            ? 'La copie automatique est indisponible. Sélectionnez le texte pour le copier.'
            : ''}
      </p>
    </section>
  )
}
